import { sortBy } from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import {
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
} from '@/constants/data_sources';
import { captureException } from '@/errors';
import { initStore } from '@/stores/store-utils';
import { Feed, FeedIntegrationType } from '@/types/feeds';

export const useFeedsStore = defineStore('Feeds', () => {
  const feeds = ref<Feed[]>([]);

  function setFeeds(f: Feed[]) {
    // Order desired is: crm > gsheet > csv
    const sortedFeeds = f.sort((a, b) => {
      // First sort by type priority
      const priorityA = getFeedTypePriority(a.integration.type);
      const priorityB = getFeedTypePriority(b.integration.type);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      // Then for feeds of the same type, sort by friendly name
      return a.integration.friendly_name.localeCompare(
        b.integration.friendly_name,
      );
    });
    feeds.value = sortedFeeds;
  }

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      const { data, error } = await crossbeamApi.GET('/v0.1/feeds');

      if (error) throw error;

      setFeeds(data?.items ?? []);
    },
  );

  refresh({ useCache: true });

  function getFeedById(id: number) {
    return feeds.value.find((s) => s.id === id);
  }

  function getFeedByDataSourceType(integrationType: FeedIntegrationType) {
    return feeds.value.find((s) => s.integration.type === integrationType);
  }

  function getFeedsByDataSourceType(integrationType: FeedIntegrationType) {
    return feeds.value.filter((s) => s.integration.type === integrationType);
  }

  const syncingFeeds = computed(() => feeds.value.filter((s) => s.is_syncing));

  const feedsByIdLookup = computed(() => {
    return feeds.value.reduce(
      (result, feed) => {
        result[feed.id] = feed;
        return result;
      },
      {} as Record<number, Feed>,
    );
  });

  const crmFeeds = computed(() => {
    const crmOnly = feeds.value.filter((feed) => {
      const { type } = feed.integration;
      const isGoogle = type === GOOGLE_SHEETS_DATA_SOURCE_TYPE;
      const isCSV = type === FILE_UPLOAD_DATA_SOURCE_TYPE;
      return !isGoogle && !isCSV;
    });
    return sortBy(crmOnly, 'id');
  });

  const googleFeed = computed(() =>
    feeds.value.find((feed) => feed.integration?.type === 'google_sheets'),
  );

  const feedNamesReadyForPreview = computed(() =>
    feeds.value
      .filter(
        (feed) => feed.preview_sync_complete && !feed.initial_sync_complete,
      )
      .map((feed) => feed.integration.friendly_name),
  );

  const hasCRM = computed(() => crmFeeds.value.length > 0);
  const hasMultipleCRMs = computed(() => crmFeeds.value.length > 1);
  const unpausedCRMs = computed(() =>
    crmFeeds.value.filter((feed) => !feed.is_paused),
  );

  async function refreshFeed(feedId: number) {
    const { data, error } = await crossbeamApi.GET('/v0.1/feeds/{id}', {
      params: {
        path: {
          id: feedId,
        },
      },
    });

    if (error) throw error;

    const updatedFeeds = feeds.value.filter((f) => f.id !== feedId);
    updatedFeeds.push(data);
    setFeeds(updatedFeeds);
  }
  async function syncFeed(feedId: number) {
    const { data, error } = await crossbeamApi.POST('/v0.1/feeds/{id}/sync', {
      params: {
        path: {
          id: feedId,
        },
      },
    });
    if (error) {
      captureException(error);
      throw error;
    }
    const updatedFeeds = feeds.value.filter((f) => f.id !== feedId);
    updatedFeeds.push(data);
    setFeeds(updatedFeeds);
  }

  async function removeFeed(feedId: number, offlinePartnerOrgUuid?: string) {
    // AWS feeds live on an offline-partner org; the backend resolves it from
    // offline_partner_org_uuid. The v0.1 delete route doesn't declare the query
    // param yet, hence the cast — drop it once the spec is regenerated.
    const options = {
      params: {
        path: {
          id: feedId,
        },
        ...(offlinePartnerOrgUuid && {
          query: { offline_partner_org_uuid: offlinePartnerOrgUuid },
        }),
      },
    } as Parameters<typeof crossbeamApi.DELETE>[1];
    const { error } = await crossbeamApi.DELETE('/v0.1/feeds/{id}', options);

    if (error) {
      captureException(error);
      throw error;
    }

    /* Do not mutate local state. Caller must run refreshFeedsStore() to get
     * canonical state (avoids race with splice and stale state if refresh fails;
     * supports async BE returning feed with status DELETING). */
  }

  function updateFeed(feed: Feed) {
    const index = feeds.value.findIndex((s) => s.id === feed.id);
    if (index >= 0) {
      feeds.value[index] = feed;
    }
  }

  function getFeedTypePriority(type: FeedIntegrationType): number {
    switch (type) {
      case 'google_sheets':
        return 1;
      case 'file_upload':
        return 2;
      default:
        return 0; // All other types (CRM) get highest priority
    }
  }

  return {
    error: initError,
    ready,
    readySync,
    running,
    feeds,
    getFeedById,
    getFeedByDataSourceType,
    getFeedsByDataSourceType,
    syncingFeeds,
    feedNamesReadyForPreview,
    feedsByIdLookup,
    crmFeeds,
    googleFeed,
    hasCRM,
    hasMultipleCRMs,
    unpausedCRMs,
    refreshFeedsStore: refresh,
    refreshFeed,
    syncFeed,
    removeFeed,
    updateFeed,
  };
});
